import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormPageContainer } from '../../forms';
import { Form } from '../../layoutComponents';
import UnauthenticatedPageContainer from '../../auth/UnauthenticatedPageContainer';
import { InputField } from '../../../components/common/forms';
import { useMessageGetter } from 'react-message-context';
import { H1 } from '../../../components/basic/Text';
import { Button } from '../../../components/common';
import { InitiateImpersonation, initiateImpersonationSchema } from './schema';
import { initializeImpersonation } from '../../../apis_kenno';
import { RouteComponentProps } from 'react-router';
import { Routes } from '../../../constants/routes';
import Loader from '../../../components/basic/Loader';
import { useKennoTenantDispatch } from '../../../context/kennoTenantContext';
import { loginImpersonator } from '../../../context/kennoTenantActions';
import { useAppDispatch } from '../../../context/appContext';
import { setSystemNotification } from '../../../context/appActions';

const AUTH_NONCE_KEY = 'auth_nonce';
const ADMIN_SYSTEM_NOTIFICATION_TL_KEY = 'admin.impersonation.systemNotification';

const ImpersonationLoginPage: React.FC<RouteComponentProps> = ({ history, location }) => {
  const translationPrefix = 'admin.impersonation.form';
  const t = useMessageGetter('admin.impersonation');
  const tenantDispatch = useKennoTenantDispatch();
  const appDispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);

  // After a successful OAuth login, backend will redirect back to this page with a nonce in a query param.
  // This nonce can be exchanged for a session ID to log in.
  const [authNonce, setAuthNonce] = useState<string | null>(null);
  const loginInProgressRef = useRef(false); // prevent duplication execution with ref flag

  // Parse auth nonce from URL, if exists
  useLayoutEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const searchParamAuthNonce = searchParams.get(AUTH_NONCE_KEY);
    if (!searchParamAuthNonce) return;

    searchParams.delete(AUTH_NONCE_KEY);

    history.replace({
      search: searchParams.toString(),
    });

    setAuthNonce(searchParamAuthNonce);
  }, [location.search, history]);

  // If we have an auth nonce, exchange it for a session ID and sign in
  useEffect(() => {
    if (!authNonce || loginInProgressRef.current) return;
    loginInProgressRef.current = true;

    let loginCancelled = false;
    (async () => {
      setLoading(true);
      try {
        await loginImpersonator(tenantDispatch, authNonce);
        await setSystemNotification(appDispatch, ADMIN_SYSTEM_NOTIFICATION_TL_KEY);
        if (!loginCancelled) history.push(Routes.HOME);
      } catch (e) {
        console.error(e);
      } finally {
        if (!loginCancelled) setLoading(false);
      }
    })();

    return () => {
      loginCancelled = true;
    };
  }, [authNonce, tenantDispatch, appDispatch, history]);

  const formMethods = useForm<InitiateImpersonation>({
    resolver: zodResolver(initiateImpersonationSchema),
  });

  const onSubmit: SubmitHandler<InitiateImpersonation> = async (data) => {
    try {
      setLoading(true);
      const url = await initializeImpersonation(data);
      window.location.href = url;
    } catch {
      setLoading(false);
    }
  };

  return (
    <UnauthenticatedPageContainer>
      {loading ? (
        <Loader />
      ) : (
        <FormPageContainer<InitiateImpersonation> formMethods={formMethods}>
          <H1>{t('title')}</H1>
          <Form onSubmit={formMethods.handleSubmit(onSubmit)}>
            <InputField<InitiateImpersonation, string>
              fieldPath="email"
              translationPrefix={translationPrefix}
              type="text"
              required
              allowDefaultSubmit
            />
            <InputField<InitiateImpersonation, string>
              fieldPath="ssn"
              translationPrefix={translationPrefix}
              type="text"
              required
              allowDefaultSubmit
            />
            <InputField<InitiateImpersonation, number>
              fieldPath="targetTenantId"
              translationPrefix={translationPrefix}
              type="number"
              required
              allowDefaultSubmit
            />
            <Button type="submit" id="submit" primary disabled={loading}>
              {t('signIn')}
            </Button>
          </Form>
        </FormPageContainer>
      )}
    </UnauthenticatedPageContainer>
  );
};

export default ImpersonationLoginPage;
